import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, LogOut, Plus, X, Search, BarChart3, ClipboardList, CheckCircle2, Circle, MinusCircle, Trash2 } from 'lucide-react';
import {
  atlasApi, AtlasUser, Offboarding, OffboardingRelatorio, OffboardingMotivo,
  OffboardingIniciativa, OffboardingStatus, OffboardingChecklistItem, OffboardingItemStatus,
} from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import { inputCls, labelCls } from '../shared/ListaEditavel';
import AvatarColaborador from '../shared/AvatarColaborador';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

const MOTIVOS: { id: OffboardingMotivo; label: string }[] = [
  { id: 'pedido', label: 'Pedido de demissão' },
  { id: 'sem_justa', label: 'Sem justa causa' },
  { id: 'com_justa', label: 'Com justa causa' },
  { id: 'aposentadoria', label: 'Aposentadoria' },
  { id: 'fim_contrato', label: 'Fim de contrato' },
  { id: 'acordo', label: 'Acordo' },
];
const motivoLabel = (m: string) => MOTIVOS.find(x => x.id === m)?.label || m;

const STATUS_ITEM_CICLO: OffboardingItemStatus[] = ['pendente', 'concluido', 'na'];
const iconeItem = (s: OffboardingItemStatus) =>
  s === 'concluido' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
    : s === 'na' ? <MinusCircle className="w-4 h-4 text-gray-300" />
    : <Circle className="w-4 h-4 text-gray-300" />;

const fmtData = (s?: string | null) => {
  if (!s) return '—';
  const [a, m, d] = s.split('-');
  return d ? `${d}/${m}/${a}` : s;
};

const progresso = (o: Offboarding) => {
  const total = o.checklist.filter(i => i.status !== 'na').length;
  if (!total) return 100;
  const done = o.checklist.filter(i => i.status === 'concluido').length;
  return Math.round((done / total) * 100);
};

const OffboardingApp: React.FC<Props> = ({ user, podeEditar }) => {
  const [itens, setItens] = useState<Offboarding[]>([]);
  const [rel, setRel] = useState<OffboardingRelatorio | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [fb, setFb] = useState<Feedback>(null);

  const [novoModal, setNovoModal] = useState(false);
  const [detalhe, setDetalhe] = useState<Offboarding | null>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    Promise.all([
      atlasApi.getOffboardings(user).then(r => setItens(r.itens || [])),
      atlasApi.getOffboardingRelatorio(user).then(setRel).catch(() => setRel(null)),
    ])
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div className="space-y-5">
      {/* KPIs */}
      {rel && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard titulo="Desligamentos" valor={rel.total} />
          <KpiCard titulo="Em andamento" valor={rel.em_andamento} cor="text-amber-600" />
          <KpiCard titulo="Entrevistas de saída" valor={rel.entrevistas_realizadas} />
          <KpiCard titulo="eNPS de saída" valor={rel.enps_saida ?? '—'} cor={(rel.enps_saida ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'} />
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><LogOut className="w-5 h-5 text-purple-600" /> Processos de desligamento</h3>
        {podeEditar && (
          <button onClick={() => { setNovoModal(true); setFb(null); }} className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl text-sm font-medium">
            <Plus className="w-4 h-4" /> Novo offboarding
          </button>
        )}
      </div>

      {erro && <FeedbackBanner feedback={{ tipo: 'erro', msg: erro }} />}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : itens.length === 0 ? (
        <div className="text-center py-14 text-gray-400 text-sm">Nenhum processo de offboarding registrado.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-50">
          {itens.map(o => (
            <button key={o._id} onClick={() => setDetalhe(o)} className="flex items-center gap-3 w-full px-4 py-3 hover:bg-purple-50/40 text-left transition">
              <AvatarColaborador nome={o.colaborador_nome} fotoUrl={o.funccodi ? atlasApi.fotoUrl(user, o.funccodi) : undefined} size={38} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{o.colaborador_nome || `Mat. ${o.funccodi}`}</div>
                <div className="text-xs text-gray-400 truncate">{motivoLabel(o.motivo)} · {fmtData(o.data_desligamento)}</div>
              </div>
              <div className="w-28">
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: `${progresso(o)}%` }} />
                </div>
                <div className="text-[10px] text-gray-400 mt-1 text-right">{progresso(o)}% checklist</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${o.status === 'concluido' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {o.status === 'concluido' ? 'Concluído' : 'Em andamento'}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Relatório motivos */}
      {rel && Object.keys(rel.por_motivo).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4" /> Motivos de saída</h4>
          <div className="space-y-2">
            {Object.entries(rel.por_motivo).sort((a, b) => b[1] - a[1]).map(([m, n]) => {
              const pct = rel.total ? Math.round((n / rel.total) * 100) : 0;
              return (
                <div key={m} className="flex items-center gap-3 text-sm">
                  <div className="w-40 text-gray-600 truncate">{motivoLabel(m)}</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-400" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-10 text-right text-gray-500">{n}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {novoModal && (
        <NovoOffboardingModal
          user={user}
          onClose={() => setNovoModal(false)}
          onCriado={() => { setNovoModal(false); carregar(); }}
        />
      )}

      {detalhe && (
        <DetalheModal
          user={user}
          podeEditar={podeEditar}
          inicial={detalhe}
          onClose={() => setDetalhe(null)}
          onSalvo={() => { setDetalhe(null); carregar(); }}
        />
      )}
    </div>
  );
};

const KpiCard: React.FC<{ titulo: string; valor: React.ReactNode; cor?: string }> = ({ titulo, valor, cor }) => (
  <div className="bg-white rounded-2xl border border-gray-100 px-4 py-3 shadow-sm">
    <div className={`text-2xl font-bold ${cor || 'text-gray-900'}`}>{valor}</div>
    <div className="text-xs text-gray-400">{titulo}</div>
  </div>
);

// ---- Modal de criação ----
const NovoOffboardingModal: React.FC<{ user: AtlasUser; onClose: () => void; onCriado: () => void }> = ({ user, onClose, onCriado }) => {
  const [colab, setColab] = useState<{ funccodi: number; nome?: string; cargo?: string; tem_foto?: boolean; data_sinc?: string } | null>(null);
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [motivo, setMotivo] = useState<OffboardingMotivo>('pedido');
  const [iniciativa, setIniciativa] = useState<OffboardingIniciativa>('colaborador');
  const [data, setData] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  useEffect(() => {
    if (colab) return;
    const termo = busca.trim();
    if (termo.length < 2) { setResultados([]); return; }
    let cancel = false;
    setBuscando(true);
    const t = window.setTimeout(() => {
      atlasApi.listarColaboradores(user, { busca: termo, status: 'ativo', per_page: 8 })
        .then((r: any) => { if (!cancel) setResultados(r?.itens || []); })
        .catch(() => { if (!cancel) setResultados([]); })
        .finally(() => { if (!cancel) setBuscando(false); });
    }, 300);
    return () => { cancel = true; window.clearTimeout(t); };
  }, [user, colab, busca]);

  const salvar = async () => {
    if (!colab) return;
    setSalvando(true);
    setFb(null);
    try {
      await atlasApi.criarOffboarding(user, {
        funccodi: colab.funccodi,
        motivo, iniciativa,
        data_desligamento: data || undefined,
      });
      onCriado();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao criar' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Novo offboarding</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {fb && <FeedbackBanner feedback={fb} />}
          <div>
            <label className={labelCls}>Colaborador</label>
            {colab ? (
              <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-3 py-2">
                <AvatarColaborador nome={colab.nome} fotoUrl={colab.tem_foto ? atlasApi.fotoUrl(user, colab.funccodi, colab.data_sinc) : undefined} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{colab.nome}</div>
                  <div className="text-xs text-gray-400 truncate">{colab.cargo || '—'}</div>
                </div>
                <button onClick={() => setColab(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input autoFocus value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar colaborador..." className={`${inputCls} pl-9`} />
                {buscando && <Loader2 className="w-4 h-4 animate-spin text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                {resultados.length > 0 && (
                  <div className="mt-2 border border-gray-100 rounded-xl divide-y divide-gray-50 max-h-56 overflow-y-auto">
                    {resultados.map((c: any) => (
                      <button key={c._id || c.funccodi} onClick={() => setColab({ funccodi: c.funccodi, nome: c.dados_publicos?.nome, cargo: c.dados_publicos?.cargo_nome, tem_foto: c.dados_publicos?.tem_foto_gix, data_sinc: c.data_sincronizacao })} className="flex items-center gap-3 w-full px-3 py-2 hover:bg-purple-50/50 text-left">
                        <AvatarColaborador nome={c.dados_publicos?.nome} fotoUrl={c.dados_publicos?.tem_foto_gix ? atlasApi.fotoUrl(user, c.funccodi, c.data_sincronizacao) : undefined} size={32} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{c.dados_publicos?.nome}</div>
                          <div className="text-xs text-gray-400 truncate">{c.dados_publicos?.cargo_nome || '—'}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Motivo</label>
              <select value={motivo} onChange={e => setMotivo(e.target.value as OffboardingMotivo)} className={inputCls}>
                {MOTIVOS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Iniciativa</label>
              <select value={iniciativa} onChange={e => setIniciativa(e.target.value as OffboardingIniciativa)} className={inputCls}>
                <option value="colaborador">Colaborador</option>
                <option value="empresa">Empresa</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Data do desligamento</label>
            <input type="date" value={data} onChange={e => setData(e.target.value)} className={inputCls} />
          </div>
          <p className="text-xs text-gray-400">Um checklist padrão será criado automaticamente.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
          <button onClick={salvar} disabled={!colab || salvando} className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-sm font-medium">
            {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Criar
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Modal de detalhe (checklist + entrevista) ----
const DetalheModal: React.FC<{ user: AtlasUser; podeEditar: boolean; inicial: Offboarding; onClose: () => void; onSalvo: () => void }> = ({ user, podeEditar, inicial, onClose, onSalvo }) => {
  const [o, setO] = useState<Offboarding>(inicial);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  const ciclar = (idx: number) => {
    if (!podeEditar) return;
    setO(prev => {
      const checklist = prev.checklist.map((it, i) => {
        if (i !== idx) return it;
        const next = STATUS_ITEM_CICLO[(STATUS_ITEM_CICLO.indexOf(it.status) + 1) % STATUS_ITEM_CICLO.length];
        return { ...it, status: next };
      });
      return { ...prev, checklist };
    });
  };

  const setEntrevista = (patch: Partial<Offboarding['entrevista']>) =>
    setO(prev => ({ ...prev, entrevista: { ...prev.entrevista, ...patch } }));

  const salvar = async () => {
    setSalvando(true);
    setFb(null);
    try {
      await atlasApi.atualizarOffboarding(user, o._id, {
        checklist: o.checklist,
        entrevista: o.entrevista,
        status: o.status,
      });
      onSalvo();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao salvar' });
    } finally {
      setSalvando(false);
    }
  };

  const cancelarProcesso = async () => {
    if (!window.confirm('Cancelar este processo de offboarding? Ele será removido da lista.')) return;
    setExcluindo(true);
    setFb(null);
    try {
      await atlasApi.excluirOffboarding(user, o._id);
      onSalvo();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao cancelar' });
      setExcluindo(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <AvatarColaborador nome={o.colaborador_nome} fotoUrl={o.funccodi ? atlasApi.fotoUrl(user, o.funccodi) : undefined} size={40} />
            <div>
              <div className="font-semibold text-gray-900">{o.colaborador_nome || `Mat. ${o.funccodi}`}</div>
              <div className="text-xs text-gray-400">{motivoLabel(o.motivo)} · {fmtData(o.data_desligamento)}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {fb && <FeedbackBanner feedback={fb} />}

          {/* Checklist */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2"><ClipboardList className="w-4 h-4" /> Checklist</h4>
            <div className="border border-gray-100 rounded-xl divide-y divide-gray-50 overflow-hidden">
              {o.checklist.map((it, idx) => (
                <button key={idx} onClick={() => ciclar(idx)} disabled={!podeEditar} className="flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-gray-50 disabled:cursor-default">
                  {iconeItem(it.status)}
                  <span className={`flex-1 text-sm ${it.status === 'concluido' ? 'text-gray-400 line-through' : it.status === 'na' ? 'text-gray-300' : 'text-gray-700'}`}>{it.item}</span>
                  {it.responsavel && <span className="text-xs text-gray-400">{it.responsavel}</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Entrevista de saída */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Entrevista de saída</h4>
            <label className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <input type="checkbox" checked={o.entrevista.realizada} disabled={!podeEditar} onChange={e => setEntrevista({ realizada: e.target.checked })} />
              Entrevista realizada
            </label>
            {o.entrevista.realizada && (
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>Recomendaria a empresa? (0 a 10)</label>
                  <div className="flex flex-wrap gap-1">
                    {Array.from({ length: 11 }, (_, i) => i).map(n => (
                      <button key={n} disabled={!podeEditar} onClick={() => setEntrevista({ recomendaria_empresa: n })}
                        className={`w-8 h-8 rounded-lg text-xs font-medium transition ${o.entrevista.recomendaria_empresa === n ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Comentários</label>
                  <textarea value={o.entrevista.comentarios || ''} disabled={!podeEditar} onChange={e => setEntrevista({ comentarios: e.target.value })} rows={3} className={inputCls} />
                </div>
              </div>
            )}
          </div>

          {podeEditar && (
            <div>
              <label className={labelCls}>Status do processo</label>
              <select value={o.status} onChange={e => setO(prev => ({ ...prev, status: e.target.value as OffboardingStatus }))} className={inputCls}>
                <option value="em_andamento">Em andamento</option>
                <option value="concluido">Concluído</option>
              </select>
            </div>
          )}
        </div>

        {podeEditar && (
          <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
            <button onClick={cancelarProcesso} disabled={salvando || excluindo} className="inline-flex items-center gap-2 mr-auto px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 rounded-xl disabled:opacity-50 transition">
              {excluindo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Cancelar processo
            </button>
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Fechar</button>
            <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-sm font-medium">
              {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Salvar alterações
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OffboardingApp;
